import { isEmpty, isNumber, isString, isUndefined, merge } from 'lodash-es';
import { type PropsWithChildren } from 'react';

import { isHoneycombTileCategoricalData } from './is-honeycomb-tile-categorical-data.js';
import { isHoneycombTileNumericData } from './is-honeycomb-tile-numeric-data.js';
import { isValidColorRule } from '../../core/slots/ColorRule.js';
import {
  DEFAULT_HONEYCOMB_CATEGORICAL_COLOR_PALETTE,
  DEFAULT_HONEYCOMB_CONFIG,
} from '../constants.js';
import { HoneycombChartProperties } from '../types/honeycomb-config-properties.js';
import {
  HoneycombRequiredConfigProps,
  HoneycombSlotsConfig,
} from '../types/honeycomb-internal.js';
import {
  HoneycombChartNumericalProps,
  HoneycombChartProps,
  HoneycombData,
  type HoneycombTileData,
  HoneycombTileShape,
} from '../types/honeycomb.js';

const checkCategoricalDataset = (data: HoneycombData) =>
  Array.isArray(data) &&
  (isString(data[0]) ||
    isHoneycombTileCategoricalData((data[0] ?? {}) as HoneycombTileData));

export const checkNumericalDataset = (
  props: PropsWithChildren<HoneycombChartProps>,
): props is HoneycombChartNumericalProps =>
  Array.isArray(props.data) &&
  (isNumber(props.data[0]) ||
    isHoneycombTileNumericData((props.data[0] ?? {}) as HoneycombTileData));

export const getDefaultColorScheme = (
  props: PropsWithChildren<HoneycombChartProps>,
  contextConfig: HoneycombChartProperties,
) => {
  const currentColorScheme = props.colorScheme || contextConfig.colorScheme;
  const isCategoricalDataset = checkCategoricalDataset(props.data);
  const isColorPaletteName = typeof currentColorScheme === 'string';
  const isCustomNumericColorScheme = Array.isArray(currentColorScheme);
  const isInvalidColorScheme =
    isUndefined(currentColorScheme) || isEmpty(currentColorScheme);

  if (isInvalidColorScheme) {
    return isCategoricalDataset
      ? DEFAULT_HONEYCOMB_CATEGORICAL_COLOR_PALETTE
      : DEFAULT_HONEYCOMB_CONFIG.colorScheme;
  } else if (
    isColorPaletteName ||
    !isCategoricalDataset ||
    isCustomNumericColorScheme
  ) {
    return currentColorScheme;
  } else {
    const capitalizedCategories: Record<string, string> = {};
    Object.keys(currentColorScheme).forEach((key) => {
      capitalizedCategories[key] = currentColorScheme[key];
    });

    return capitalizedCategories;
  }
};

export const buildHoneycombConfig = (
  props: PropsWithChildren<HoneycombChartProps>,
  contextConfig: HoneycombChartProperties,
  slots: HoneycombSlotsConfig,
): HoneycombRequiredConfigProps => {
  const shape: HoneycombTileShape =
    props.shape ?? contextConfig.shape ?? DEFAULT_HONEYCOMB_CONFIG.shape;

  const colorScheme = getDefaultColorScheme(props, contextConfig);

  const isToolbarSet = !!(slots.toolbar || contextConfig.toolbar);

  const toolbar = isToolbarSet
    ? merge(
        {},
        DEFAULT_HONEYCOMB_CONFIG.toolbar,
        contextConfig.toolbar,
        slots.toolbar,
      )
    : undefined;

  const unit = props.unit ?? contextConfig.unit ?? undefined;
  const truncationMode =
    props.truncationMode ??
    contextConfig.truncationMode ??
    DEFAULT_HONEYCOMB_CONFIG.truncationMode;

  const formatter = props.formatter ?? contextConfig.formatter;
  const showLabels = props.showLabels ?? contextConfig.showLabels ?? false;

  const legend = merge(
    {},
    DEFAULT_HONEYCOMB_CONFIG.legend,
    contextConfig.legend,
    slots.legend,
  );

  let min, max;

  if (checkNumericalDataset(props)) {
    min = props.min ?? contextConfig.min ?? undefined;
    max = props.max ?? contextConfig.max ?? undefined;
  }

  const colorRules = (contextConfig.colorRules || [])
    .filter(isValidColorRule)
    .concat(slots.colorRules || []);

  return {
    shape,
    colorScheme,
    toolbar,
    legend,
    unit,
    formatter,
    truncationMode,
    min,
    max,
    showLabels,
    colorRules,
  };
};
